// src/pages/LoginPage.tsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";

import LoginSection from "src/components/LoginSection";
import GoogleLoginButton from "src/components/auth/GoogleLoginButton";
import { useAuthStore } from "src/store/Auth/auth.store";
import { delegateLoginApi } from "src/api/auth/authApi";
import { setTokens } from "src/utils/auth";

const Container = styled.div`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Box = styled.div`
  width: 360px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setLogin = useAuthStore((s) => s.setLogin);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // ✅ 서버에서 callback으로 /login?code=... 로 돌아오는 구조면
    // 여기서 바로 LoginCallback 라우트로 넘겨주는 게 안전함.
    // (LoginPage에서 토큰 교환 로직을 돌리면 컴포넌트 책임이 커지고 루프나기 쉬움)
    const code = searchParams.get("code");
    if (code) {
      navigate(`/login/callback?code=${encodeURIComponent(code)}`, { replace: true });
      return;
    }

    // 참고: 인증 상태 체크는 App.tsx의 LoginEntryRoute에서 이미 처리됨
    // LoginPage가 렌더링되면 이미 isAuthed() = false 상태
  }, [navigate, searchParams]);

  const handleSubmit = async (id: string, pw: string, type: "user" | "vendor") => {
    // user 타입은 구글 로그인 사용 (handleSubmit 호출 안 됨)
    if (type === "user") return;

    if (!id.trim() || !pw.trim()) {
      setError("아이디와 비밀번호를 입력해주세요.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await delegateLoginApi(id, pw);

      // 토큰 쿠키에 저장
      setTokens(result.accessToken, result.refreshToken);

      // 사용자 정보 localStorage에 저장
      localStorage.setItem("accessToken", result.accessToken);
      localStorage.setItem("userId", String(result.userId));
      localStorage.setItem("userEmail", result.email);
      localStorage.setItem("userName", result.name);
      localStorage.setItem("loginType", "delegate"); // 입점사 로그인 타입 저장

      // 로그인 상태 설정 후 대시보드로 이동 (menuTree 포함)
      setLogin(result.accessToken, result.menuTree ?? []);
      navigate("/", { replace: true });
    } catch (e: any) {
      setError(e?.message || "로그인에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Box>
        <LoginSection onSubmit={handleSubmit} loading={loading} error={error}>
          {/* ✅ 서버 OAuth 시작 */}
          <GoogleLoginButton />
        </LoginSection>
      </Box>
    </Container>
  );
};

export default LoginPage;
